import { Box, Group } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconPlus } from '@tabler/icons-react';
import React from 'react';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import { UploadProblemAlert } from 'src/design/molecules/UploadProblemAlert';
import { useControlledAttachments } from 'src/design/organisms/attachments/useControlledAttachments';
import { ImageThumbnail } from './ImageThumbnail';
import { ImageReviewCard } from './ImageReviewCard';
import type { IImageThumbnailProps } from './ImageThumbnail';
import type { IImageReviewCardProps } from './ImageReviewCard';

export interface IImageUploadProps {
  /** Controlled list of current attachments; the component keeps no copy. */
  files: IAttachedFile[];
  /** Fires once per upload/removal/comment-save with the next list and what changed. */
  onChange: (files: IAttachedFile[], change: IAttachmentChange) => void;
  disabled?: boolean;
  maxFiles?: number;
  uploadFile: (file: File) => Promise<IAttachedFile>;
  downloadFile: (id: string) => Promise<{ url: string; name: string }>;
  /** Enables per-image comment review; omitted callers get plain thumbnails. */
  onCommentSave?: (id: string, comment: string) => Promise<void>;
}

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

/** @registry Controlled image thumbnail grid with drag/drop upload; uploads and downloads both go through the injected `uploadFile`/`downloadFile`, with upload and download failures surfaced in the shared problem alert; optionally enables per-image comment review via `onCommentSave`. `maxFiles === 1` is a single-slot replace; `maxFiles > 1` appends up to capacity. */
export const ImageUpload: React.FC<IImageUploadProps> = ({
  files,
  onChange,
  disabled = false,
  maxFiles = 100,
  uploadFile,
  downloadFile,
  onCommentSave,
}) => {
  const { singleSlot, remaining, upload } = useControlledAttachments({
    files,
    maxFiles,
    uploadFile,
    downloadFile,
    onChange,
  });

  const resolveImageUrl = (id: string) => downloadFile(id).then((r) => r.url);

  const remove = (att: IAttachedFile) => {
    onChange(
      files.filter((f) => f.id !== att.id),
      { added: [], removed: [att] },
    );
  };

  // Keeps the caller's list authoritative so the textarea's dirty state
  // clears once the comment is persisted.
  const handleCommentSave = onCommentSave
    ? async (id: string, comment: string) => {
        await onCommentSave(id, comment);
        onChange(
          files.map((f) => (f.id === id ? { ...f, comment } : f)),
          { added: [], removed: [] },
        );
      }
    : undefined;

  return (
    <>
      <Group gap="md" align="flex-start">
        {files.map((att) =>
          onCommentSave ? (
            <ImageReviewCard
              key={att.id}
              id={att.id}
              comment={att.comment}
              resolveImageUrl={resolveImageUrl}
              disabled={disabled}
              onRemoveClick={() => remove(att)}
              onDownloadClick={() => upload.download(att)}
              onCommentSave={handleCommentSave!}
            />
          ) : (
            <ImageThumbnail
              key={att.id}
              id={att.id}
              alt={att.comment}
              resolveImageUrl={resolveImageUrl}
              onClick={() => upload.download(att)}
              onRemoveClick={disabled ? undefined : () => remove(att)}
            />
          ),
        )}
        {!disabled && (singleSlot || remaining > 0) && (
          <Dropzone
            onDrop={upload.onDrop}
            onReject={upload.onReject}
            loading={upload.loading}
            accept={ACCEPTED_IMAGE_TYPES}
            multiple={!singleSlot}
            maxFiles={singleSlot ? 1 : remaining}
            aria-label={upload.loading ? 'Uploading image' : 'Add image'}
            w={96}
            h={96}
            p={0}
            styles={{ inner: { height: '100%' } }}
          >
            <Box
              display="flex"
              w="100%"
              h="100%"
              style={{ alignItems: 'center', justifyContent: 'center' }}
            >
              <IconPlus size={32} />
            </Box>
          </Dropzone>
        )}
      </Group>
      <UploadProblemAlert
        problems={upload.problems}
        onClear={upload.clearProblems}
      />
    </>
  );
};

export { ImageThumbnail, ImageReviewCard };
export type { IImageThumbnailProps, IImageReviewCardProps };
